export default class SignatureUtil {

    /**
     * @typedef {object} TextInCanvas
     * @property {string} text It's the upper text
     * @property {string} extraText
     */
    static yPositionCanvas = {
        y1: 205,
        y2: 235,
        y3: 275
    }

    static MAX_WIDTH = 627
    static MAX_HEIGHT = 300
    static BASE_FONT_SIZE = 23

    /**
     * @static
     * @param {boolean} proofOfLife
     * @return {{image: string, imageWidth: number, imageHeight: number}}
     * @memberof CanvasUtil
     */
    static saveImage(proofOfLife) {
        let image
        let destinationCanvas

        if (!proofOfLife) {
            let sourceCanvas = document.getElementsByTagName('canvas')[1]
            destinationCanvas = document.createElement('canvas')
            destinationCanvas.height = sourceCanvas.height
            destinationCanvas.width = sourceCanvas.width
            let destinationContext = destinationCanvas.getContext('2d')
            destinationContext.drawImage(sourceCanvas, 0, 0)
            destinationContext.globalCompositeOperation = 'destination-over'
            destinationContext.fillStyle = '#4484a500'
            destinationContext.fillRect(
                0,
                0,
                destinationCanvas.width,
                destinationCanvas.height
            )
            image = destinationCanvas.toDataURL('image/png')
        } else {
            let sourceCanvas = document.getElementsByTagName('canvas')[1]
            let sourceSign = document.getElementsByTagName('canvas')[2]
            destinationCanvas = document.createElement('canvas')
            destinationCanvas.height = sourceCanvas.height
            destinationCanvas.width = sourceCanvas.width
            let destinationContext = destinationCanvas.getContext('2d')
            destinationContext.drawImage(sourceCanvas, 0, 0)
            destinationContext.drawImage(sourceSign, 0, 0)
            destinationContext.globalCompositeOperation = 'destination-over'
            destinationContext.fillStyle = '#4484a500'
            destinationContext.fillRect(
                0,
                0,
                destinationCanvas.width,
                destinationCanvas.height
            )
            image = destinationCanvas.toDataURL('image/png')
        }

        return {
            image,
            width: destinationCanvas.width,
            height: destinationCanvas.height,
        }
    }

    /**
    * @param {OrientationType} orientation
    */
    static adjustYCoordinates(orientation) {
        let sourceCanvas = document.getElementsByTagName('canvas')
        const gottenCanvas = sourceCanvas[1]
        let copyPositions = { ...this.yPositionCanvas }

        if (orientation === 'portrait-primary' || orientation === 'portrait-secondary') {
            if (gottenCanvas.height >= 300) copyPositions = {
                y1: gottenCanvas.height - 80,
                y2: gottenCanvas.height - 60,
                y3: gottenCanvas.height - 40
            }
            else copyPositions = {
                y1: gottenCanvas.height - 60,
                y2: gottenCanvas.height - 40,
                y3: gottenCanvas.height - 20
            }
        } else if (orientation === 'landscape-primary' || orientation === 'landscape-secondary')
            copyPositions = {
                y1: gottenCanvas.height - 80,
                y2: gottenCanvas.height - 60,
                y3: gottenCanvas.height - 40
            }
        return { ...copyPositions }
    }

    /**
    * @param {TextInCanvas} textsInCanvas
    * @param {boolean} isMobile
    * @param {OrientationType} orientation
    * @param {boolean} isTimestamp 
    * @param {number} scale
    */
    static setNamesInCanvas(textsInCanvas, isMobile, orientation, isTimestamp, scale) {
        let fixedCoordinates = this.adjustYCoordinates(orientation)

        if ((textsInCanvas.text.length + textsInCanvas.extraText.length) > 26) {
            this.setTextInCanvas(fixedCoordinates.y1, `${textsInCanvas.text}`, isMobile, orientation, isTimestamp, scale)
            this.setTextInCanvas(fixedCoordinates.y2, `${textsInCanvas.extraText}`, isMobile, orientation, isTimestamp, scale)
        } else {
            this.setTextInCanvas(fixedCoordinates.y2, `${textsInCanvas.text} ${textsInCanvas.extraText}`, isMobile, orientation, isTimestamp, scale)
        }
    }

    /**
     * @param {number} y
     * @param {string} text
     * @param {boolean} isMobile
     * @param {OrientationType} orientation
     * @param {boolean} isTimestamp
     * @param {number} scale
    */
    static setTextInCanvas(y, text, isMobile, orientation, isTimestamp, scale) {
        try {
            let sourceCanvas = document.getElementsByTagName('canvas')
            const gottenCanvas = sourceCanvas[1]
            let ctx = gottenCanvas.getContext('2d')
            ctx.font = `bold ${gottenCanvas.width <= 400 ? 11 : this.BASE_FONT_SIZE * scale}px Arial`

            y += isTimestamp
                ? isMobile
                    ? orientation === 'landscape-primary' || orientation === 'landscape-primary'
                        ? 20
                        : 0
                    : orientation === 'portrait-primary' || orientation === 'portrait-secondary'
                        ? 20
                        : 10
                : 0
            ctx.textAlign = 'center'
            ctx.fillText(text, (gottenCanvas.width) / 2, y, gottenCanvas.width)
        } catch (error) { }
    }

    /**
     * @static
     * @param {number} width
     * @param {number} height
     * @memberof SignatureUtil
     */
    static getScale(width, height) {
        return Math.min((width / this.MAX_WIDTH), (height / this.MAX_HEIGHT), 1)
    }

    /**
     * @author Edgar Escobedo
     * @param {import('i18next').TFunction} t
     * @description Gets the default format of the simulated signature mark
     * @date 01-08-2025
     * @returns timestamp  2025-Ago-01T00:00:00-0600
     */
    static formaTimestamp(t) {

        const meses = [
            'signatureRequestProfileListSigners.timestamp.ene',
            'signatureRequestProfileListSigners.timestamp.feb',
            'signatureRequestProfileListSigners.timestamp.mar',
            'signatureRequestProfileListSigners.timestamp.abr',
            'signatureRequestProfileListSigners.timestamp.may',
            'signatureRequestProfileListSigners.timestamp.jun',
            'signatureRequestProfileListSigners.timestamp.jul',
            'signatureRequestProfileListSigners.timestamp.ago',
            'signatureRequestProfileListSigners.timestamp.sep',
            'signatureRequestProfileListSigners.timestamp.oct',
            'signatureRequestProfileListSigners.timestamp.nov',
            'signatureRequestProfileListSigners.timestamp.dic'
        ]
        const today = new Date()
        const year = today.getFullYear()
        const month = t(meses[today.getMonth()])
        const day = String(today.getDate()).padStart(2, '0')
        const offsetMin = today.getTimezoneOffset()
        const signo = offsetMin > 0 ? '-' : '+'
        const offsetAbs = Math.abs(offsetMin)
        const offsetHours = String(Math.floor(offsetAbs / 60)).padStart(2, '0')
        const offsetMinutes = String(offsetAbs % 60).padStart(2, '0')
        const timezone = `${signo}${offsetHours}${offsetMinutes}`
        return `${year || ''}-${month || ''}-${day || ''}T00:00:00${timezone || ''}`
    }

    /**
     * @static
     * @description 
     * @param {string} name The name of the signer or the person to validate and return the validation and the separated names
     * @memberof SignatureUtil
     */
    static formatName(name) {
        let names = ''
        let lastNames = ''
        let nameSplitted = name.split(' ')
        let twoLinesText = false

        if (name.length < 26 && nameSplitted.length === 3) {
            names = nameSplitted.join(' ')
            lastNames = ''
        } else if (nameSplitted.length >= 4 || name.length >= 26) {
            names = nameSplitted.slice(0, 2).join(' ')
            lastNames = nameSplitted.slice(2).join(' ')
            twoLinesText = true
        } else {
            names = nameSplitted[0] || ''
            lastNames = nameSplitted.slice(1).join(' ')
            twoLinesText = false
        }

        return { twoLinesText, names, lastNames }
    }
}
